import type { Call, TypedData } from "starknet";
import type { WalletData, WalletType } from "./wallet";
import { Chain, ChainToken, Currency } from "./core";

/**
 * Transaction-related types
 */

type PasskeyEnabledParams = {
  usePasskey: true;
  externalUserId: string;
};

type PasskeyDisabledParams = {
  usePasskey?: false;
  externalUserId?: string;
};

type PasskeyParams = PasskeyEnabledParams | PasskeyDisabledParams;

export type ExecuteTransactionParams = PasskeyParams & {
  encryptKey?: string;
  wallet: WalletData;
  calls: Call[];
};

export type TxType = string;
export type TxStatus = "PENDING" | "PROCESSING" | "SUCCESS" | "FAILED" | "CANCELLED";

export interface Transaction {
  id: string;
  type: TxType;
  subType?: string;
  chain: Chain;
  transactionHash: string;

  // Parties
  senderAddress: string;
  destinationAddress: string;

  // Contract Interaction
  contractAddress?: string;
  contractName?: string;
  functionName?: string;
  functionSelector?: string;
  callData?: unknown;

  // Token Transfer (nullable for non-token operations)
  token?: ChainToken;
  tokenAddress?: string;
  amount?: string;

  // NFT Specific
  nftTokenId?: string;
  nftContract?: string;
  nftMetadata?: Record<string, unknown>;

  // SKU Specific
  skuId?: string;
  skuReference?: string;
  skuPurchaseAmount?: number;
  skuPurchaseCurrency?: Currency;
  skuFileNumber?: string;

  // Status
  status: TxStatus;

  // Timestamps
  createdAt: Date;
  confirmedAt?: Date;
  updatedAt: Date;

  // Relations
  apiPublicKey: string;
  chipiWalletId?: string;
  destinationChipiWalletId?: string;
}

export interface PrepareTypedDataParams {
  calls: Call[];
  walletAddress: string;
}

export interface PrepareTypedDataResponse {
  typedData: TypedData;
  walletType: WalletType;
}

export interface ExecuteSponsoredTransactionParams {
  calls: Call[];
  walletAddress: string;
  signature: string[];
  apiPublicKey: string;
}

export interface ExecuteSponsoredTransactionResponse {
  transactionHash: string;
}

export interface RecordSendTransactionParams {
  transactionHash: string;
  expectedSender: string;
  expectedRecipient: string;
  expectedToken: ChainToken;
  expectedAmount: string;
  chain: Chain;
}

// Specific transaction types
export type TransferParams = PasskeyParams & {
  encryptKey?: string;
  wallet: WalletData;
  token: ChainToken;
  otherToken?: {
    contractAddress: string;
    decimals: number;
  };
  recipient: string;
  amount: string;
};

export type TransferHookInput = PasskeyParams & {
  encryptKey?: string;
  wallet: WalletData;
  token: ChainToken;
  otherToken?: {
    contractAddress: string;
    decimals: number;
  };
  recipient: string;
  amount: number;
};

export type ApproveParams = PasskeyParams & {
  encryptKey?: string;
  wallet: WalletData;
  contractAddress: string;
  spender: string;
  amount: string;
  decimals?: number;
};

export type ApproveHookInput = PasskeyParams & {
  encryptKey?: string;
  wallet: WalletData;
  contractAddress: string;
  spender: string;
  decimals?: number;
  amount: number;
};

export type CallAnyContractParams = PasskeyParams & {
  encryptKey?: string;
  wallet: WalletData;
  contractAddress: string;
  calls: Call[];
};

export interface GetTransactionsParams {
  page?: number;
  limit?: number;
  orgId: string;
}

export interface GetTransactionListQuery {
  page?: number;
  limit?: number;
  calledFunction?: string;
  walletAddress: string; // Required: wallet address to filter transactions
  day?: number; // Day of the month (1-31)
  month?: number; // Month of the year (1-12)
  year?: number; // Year (e.g., 2025)
}

/**
 * Parameters for fetching a single transaction by hash or internal ID.
 */
export interface GetTransactionParams {
  /** Transaction hash (0x...) or internal database ID */
  hashOrId: string;
}

/**
 * Parameters for fetching on-chain transaction status.
 */
export interface GetTransactionStatusParams {
  /** Transaction hash (0x...) */
  hash: string;
}

/**
 * On-chain transaction status from StarkNet sequencer.
 */
export enum OnChainTxStatus {
  RECEIVED = "RECEIVED",
  PENDING = "PENDING",
  ACCEPTED_ON_L2 = "ACCEPTED_ON_L2",
  ACCEPTED_ON_L1 = "ACCEPTED_ON_L1",
  REJECTED = "REJECTED",
  REVERTED = "REVERTED",
  NOT_RECEIVED = "NOT_RECEIVED",
}

/**
 * Response from the transaction status endpoint.
 */
export interface TransactionStatusResponse {
  /** Transaction hash */
  transactionHash: string;
  /** Current on-chain status */
  status: OnChainTxStatus;
  /** Block number (if included in a block) */
  blockNumber?: number;
  /** Revert reason (if status is REVERTED) */
  revertReason?: string;
}

/**
 * Minimal account interface for PaymasterAdapter.
 * Compatible with starknet.js Account, external wallet objects, and Cartridge Controller.
 */
export interface PaymasterAccount {
  address: string;
  signMessage(typedData: TypedData): Promise<unknown>;
  getClassHashAt(contractAddress: string): Promise<string>;
}

/**
 * Adapter for executing gasless (paymaster-sponsored) transactions.
 *
 * Handles the full flow: prepare typed data -> sign -> submit to paymaster.
 * For external wallets (Argent X, Braavos), `account.signMessage()` triggers the wallet popup.
 * For DirectSigner, signing happens programmatically.
 */
export interface PaymasterAdapter {
  execute(params: { account: PaymasterAccount; calls: Call[] }): Promise<string>;
}

/**
 * Parameters for signing SNIP-12 typed data without executing a transaction.
 * Useful for x402 payment signatures and other off-chain signing flows.
 */
export interface SignTypedDataParams {
  /** Encryption key (PIN) to decrypt the private key */
  encryptKey: string;
  /** Wallet data containing encrypted private key */
  wallet: WalletData;
  /** SNIP-12 typed data to sign */
  typedData: TypedData;
}

/**
 * Result of signing SNIP-12 typed data.
 */
export interface SignTypedDataResult {
  /** Signature r component */
  r: string;
  /** Signature s component */
  s: string;
}

/**
 * Parameters for syncing on-chain transactions from Voyager.
 */
export interface SyncOnChainTransfersParams {
  /** Wallet address to sync transfers for */
  walletAddress: string;
}

/**
 * Response from sync-on-chain endpoint.
 */
export interface SyncOnChainTransfersResponse {
  /** Number of new transactions synced to DB */
  synced: number;
  /** Total transfers found on-chain */
  total: number;
}
